/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	type SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import Fuse from 'fuse.js';
import debounce from 'lodash/debounce';

import { cssMap, jsx } from '@atlaskit/css';
import SearchIcon from '@atlaskit/icon/core/search';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { Show, UNSAFE_useMediaQuery } from '@atlaskit/primitives/responsive';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { type ObjectData, type ObjectTileData } from '../types';

import GridSection from './grid-section';
import NoResults from './no-results';
import { ObjectDetails } from './object-details';

type BaseExplorerProps<T extends ObjectData | ObjectTileData> = {
	testId?: string;
	isObjectTile?: boolean;
	getObjects: () => Promise<Record<string, T> | undefined>;
	getSizes?: (
		object: T,
		isBold?: boolean,
	) => Array<{ label: string; element: React.ReactNode; code: string }>;
};

const getFuseOptions = <T extends ObjectData | ObjectTileData>(): Fuse.IFuseOptions<T> => ({
	keys: [
		{
			name: 'componentName',
			weight: 2,
		},
		{
			name: 'objectName',
			weight: 2,
		},
		{
			name: 'keywords',
			weight: 1,
		},
	],
	threshold: 0.4,
	ignoreLocation: true,
});

const styles = cssMap({
	objectDetailsBox: {
		position: 'sticky',
		insetBlockStart: token('space.100'),
	},
	modalObjectDetails: {
		width: '100%',
		borderRadius: token('radius.small'),
		overflow: 'auto',
	},
	searchIconBefore: {
		paddingInlineStart: token('space.100'),
	},
});

const SearchIconBefore = () => (
	<Box xcss={styles.searchIconBefore}>
		<SearchIcon label="Search" color="currentColor" />
	</Box>
);

export function BaseExplorer<T extends ObjectData | ObjectTileData>({
	testId,
	isObjectTile,
	getObjects,
	getSizes,
}: BaseExplorerProps<T>): JSX.Element {
	const [selectedObject, setSelectedObject] = useState<T>();
	const fuseIndex = useRef<Fuse<T> | null>(null);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [searchedObjects, setSearchedObjects] = useState<T[] | undefined>();
	const [allObjects, setAllObjects] = useState<T[] | undefined>();
	const [isBold, setIsBold] = useState(false);

	const search = (query: string) => {
		return setSearchedObjects(
			fuseIndex.current ? fuseIndex.current.search(query).map((result) => result.item) : undefined,
		);
	};

	const debouncedSearch = useMemo(() => debounce(search, 300), []);

	const handleSearch = useCallback(
		(query: string) => {
			if (query === '') {
				setSearchedObjects(allObjects);
				return;
			}

			// Clear searched objects to enter loading state
			setSearchedObjects(undefined);

			if (query !== '') {
				debouncedSearch(query);
			}
		},
		[debouncedSearch, allObjects],
	);

	const isBelowSm = UNSAFE_useMediaQuery('below.sm');

	useEffect(() => {
		const objectsPromise = getObjects();

		objectsPromise.then((objects) => {
			const objectsArray = objects ? (Object.values(objects) as T[]) : undefined;
			setAllObjects(objectsArray);
			if (objectsArray) {
				fuseIndex.current = new Fuse(objectsArray, getFuseOptions<T>());
			}
			setSearchedObjects(objectsArray);
		});
	}, [getObjects]);

	const closeModal = () => {
		setIsModalOpen(false);
		setSelectedObject(undefined);
	};

	const updateSelectedObject = useCallback(
		(object: T) => {
			setSelectedObject((oldObject) =>
				oldObject?.packageName !== object.packageName ? object : undefined,
			);
			isBelowSm?.matches && setIsModalOpen(true);
		},
		[isBelowSm?.matches],
	);

	const sizes = selectedObject && getSizes ? getSizes(selectedObject, isBold) : undefined;

	return (
		<Inline space="space.200" testId={testId}>
			<Stack space="space.200" grow="fill">
				<Stack space="space.200">
					<Textfield
						placeholder="Search"
						elemBeforeInput={<SearchIconBefore />}
						onChange={(evt: SyntheticEvent<HTMLInputElement>) =>
							handleSearch(evt.currentTarget.value)
						}
					/>
				</Stack>

				{searchedObjects && searchedObjects.length > 0 && (
					<GridSection
						testId={testId && `${testId}-objects`}
						objects={searchedObjects}
						onObjectClick={updateSelectedObject}
						selectedObject={!isModalOpen ? selectedObject?.packageName : undefined}
						isObjectTile={isObjectTile}
					/>
				)}
				{searchedObjects === undefined && <Spinner label="Loading" />}

				{searchedObjects && searchedObjects.length === 0 && (
					<NoResults isObjectTile={isObjectTile} />
				)}
			</Stack>
			<ModalTransition>
				{isModalOpen &&
					selectedObject && (
						// CloseButton is provided by ObjectDetails component
						// eslint-disable-next-line @atlaskit/design-system/use-modal-dialog-close-button
						<Modal onClose={closeModal} shouldScrollInViewport>
							<Box xcss={styles.modalObjectDetails}>
								<ObjectDetails
									object={selectedObject}
									isModal
									onClose={closeModal}
									isObjectTile={isObjectTile}
									isBold={isBold}
									onBoldToggle={setIsBold}
									sizes={sizes}
								/>
							</Box>
						</Modal>
					)}
			</ModalTransition>
			<Show above="sm" xcss={styles.objectDetailsBox}>
				<ObjectDetails
					object={selectedObject}
					isObjectTile={isObjectTile}
					isBold={isBold}
					onBoldToggle={setIsBold}
					sizes={sizes}
				/>
			</Show>
		</Inline>
	);
}
